import React from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';

import { AngularServicesContext } from 'react-app';
import StickyHeader from 'shared/components/sticky-header';
import { notDesktop } from 'styles/global_defaults/media-queries';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { black, gray2, gray6, primary, white } from 'styles/global_defaults/colors';
import LearningStatus from 'learning_journeys/components/learning-status';
import {
  doubleSpacing,
  tripleSpacing,
  quarterSpacing,
  standardSpacing,
  largeSpacing,
  halfSpacing,
} from 'styles/global_defaults/scaffolding';
import CredlyBadgeSettings from 'institutions/components/credly-badge-settings';
import { Institution } from 'redux/schemas/models/institution';
import Fade from './fade';
import NavigationLeftElement from './navigation-left-element';
import NavigationCenterElement from './navigation-center-element';
import NavigationRightElement from './navigation-right-element';

export const HEADER_HEIGHT = tripleSpacing;

type Props = {
  origin: NavigationHeaderOrigin,
  sticky?: boolean,
  className?: string,
  scrollHeaderTrigger?: number,
  scrollStatusTrigger?: number,
};

export enum NavigationHeaderOrigin {
  HOME,
  USER_MANAGEMENT,
}

const NavigationHeader = (props: Props) => {
  const {
    className,
    sticky = false,
    scrollHeaderTrigger = 1,
    scrollStatusTrigger = 1,
    origin,
  } = props;

  const {
    $state,
  } = React.useContext(AngularServicesContext);

  const { catalogId } = $state.params;
  const leftRef = React.useRef<HTMLDivElement>();
  const rightRef = React.useRef<HTMLDivElement>();
  const headerRef = React.useRef<HTMLDivElement>();
  const learningStatusRef = React.useRef<HTMLDivElement>();
  const [maxTitleWidth, setMaxTitleWidth] = React.useState(0);
  const currentInstitution: Institution = useSelector(getCurrentInstitution);
  const currentJourney = useSelector((state) => state.models.courses[catalogId]);
  const currentInstitutionId = useSelector((state) => state.app.currentInstitutionId);


  const calculateMaxWidth = React.useCallback(() => {
    const leftWidth = leftRef.current.offsetWidth;
    const rightWidth = rightRef.current.offsetWidth;
    const headerWidth = headerRef.current.offsetWidth;

    const greatestSideElementWidth = Math.max(leftWidth, rightWidth);

    const maxWidth = ((headerWidth / 2) - greatestSideElementWidth) * 2;

    setMaxTitleWidth(maxWidth >= 0 ? maxWidth : 0);
  }, []);

  React.useEffect(() => {
    window.addEventListener('resize', calculateMaxWidth);

    return () => window.removeEventListener('resize', calculateMaxWidth);
  }, [calculateMaxWidth]);

  // Logic to handle the credly badge configuration if the institution has the credly integration
  const hasCredlyIntegration = currentInstitution?.isCredlyEnabled ?? false;
  const handleCredlyBadgeRef = React.useRef<Function>();


  React.useLayoutEffect(() => {
    calculateMaxWidth();
  }, [calculateMaxWidth]);

  let { nameFontColor } = currentJourney || {};
  // Avoiding to have nameFontColor as null when it comes that way from currentJourney
  // and having a default value wasn't enough
  if (!nameFontColor) {
    nameFontColor = '#fff';
  }
  const brandingColor = sticky ? 'transparent' : currentInstitution.brandColor;

  const styles = css`
    .navigation-header {
      width: 100%;
      display: flex;
      position: relative;
      align-items: center;
      transition: all 0.5s;
      justify-content: center;
      height: ${HEADER_HEIGHT}px;
      border-top: ${quarterSpacing}px solid ${brandingColor};
      background-color: ${sticky ? 'transparent' : '#fff'};
      border-bottom: 1px solid ${sticky ? 'transparent' : gray6};

      ${sticky && css`
        &.scrolled {
          background-color: #fff;
          border-bottom: 1px solid ${gray6};
          border-top: ${quarterSpacing}px solid ${currentInstitution.brandColor};
        }
      `};

      .left, .right {
        top: 0;
        height: 100%;
        display: flex;
        position: absolute;
        align-items: center;
        transition: all 0.5s;
        justify-content: center;
      }

      .left {
        left: 0;
        padding-right: ${doubleSpacing}px;
        color: ${sticky ? nameFontColor : '#000'};

        ${sticky && css`
          &.scrolled {
            color: #000;
          }
        `};

        .lhs-menu {
          width: 54px;
          height: 54px;
          display: none;
          align-items: center;
          justify-content: center;

          ${notDesktop(css`
            display: flex;
          `)};
        }

        .back-button {
          align-items: center;

          .icon {
            display: block;
            margin-left: ${standardSpacing}px;

            ${notDesktop(css`
              display: none;
            `)};
          }
        }
      }

      .home-button {
        .icon {
          padding-right: ${halfSpacing}px;
        }
      }

      .right {
        right: 0;
        padding-left: ${doubleSpacing}px;
        color: ${sticky ? nameFontColor : gray2};
        padding-right: ${standardSpacing}px;

        ${sticky && css`
          &.scrolled {
            color: ${gray2};
          }
        `};

        .vertical-bar {
          display: inline-block;
          height: ${largeSpacing}px;
          margin: ${halfSpacing}px;
          border: 1px solid ${black};

          &.text-white {
            border-color: ${white};
          }
        }
      }

      .title {
        min-width: 0;
        transition: opacity 0.5s;
        opacity: ${sticky ? 0 : 1};
        max-width: ${maxTitleWidth}px;

        .journey-icon {
          padding-right: ${halfSpacing}px;
        }

        ${sticky && css`
          &.scrolled {
            opacity: 1;
          }
        `};
      }

      .hoverable {
        ${!sticky && css`
          &:hover {
            color: ${primary};
          }
        `}
      }
    }
`;

  return (
    <StickyHeader
      sticky={sticky}
      className={className}
      triggers={{
        header: scrollHeaderTrigger,
        status: scrollStatusTrigger,
      }}
    >
      {({
        header: scrolledHeader,
        status: scrolledStatus,
      }) => (
        <div
          css={styles}
          ref={headerRef}
        >
          <div
            className={`navigation-header${scrolledHeader ? ' scrolled' : ''}`}
          >
            {/* Includes back button and lhs menu */}
            <NavigationLeftElement
              scrolledHeader={scrolledHeader}
              origin={origin}
              leftRef={leftRef}
            />
            {/* Includes home/title button or just title */}
            <NavigationCenterElement
              scrolledHeader={scrolledHeader}
              origin={origin}
              catalogId={catalogId}
            />
            {/* Includes release date, translation and admin options menu */}
            <NavigationRightElement
              origin={origin}
              rightRef={rightRef}
              catalogId={catalogId}
              scrolledHeader={scrolledHeader}
              nameFontColor={nameFontColor}
              hasCredlyIntegration={hasCredlyIntegration}
              handleCredlyBadgeRef={handleCredlyBadgeRef}
            />
          </div>

          {hasCredlyIntegration && (
            <CredlyBadgeSettings
              forwardShowModal={func => {
                handleCredlyBadgeRef.current = func;
              }}
            />
          )}

          <Fade show={scrolledStatus}>
            <LearningStatus
              condensed
              ref={learningStatusRef}
            />
          </Fade>
        </div>
      )}
    </StickyHeader>
  );
};

export default NavigationHeader;
